import { ColumnDef } from '@tanstack/react-table';
import React, { useEffect, useMemo } from 'react';
import { Box, Divider, Text, useColorMode } from 'theme-ui';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useSearchParams } from 'react-router-dom';
import { getApiEndPoint } from '../../../utils/GenerateQueryString';
import { RootState, IMasterDataUser, IMasterDataForTable } from '../../../../@types';
import { TabelHeadingTxt } from '../../Text/TabelCommonTxt';
import exprt from "../../assets/images/Icon/Export.svg";
import queryString from 'query-string';
import DataTable from '../../Tabels/Tabel';
import { FaStar } from 'react-icons/fa';
import { getTradeWorkerPerformance } from '../../../store/actions/Rating.Action';

const DEFAULT_PARAMS = {
    currentPage: "1",
    limit: "15"
};

const CriteriaCell = React.memo(({ criteriaList, starHeader }: any) => {
    return (
        <Box sx={{ minWidth: '220px' }}>
            {criteriaList?.map((criteria: any, index: number) => (
                <Box key={index} sx={{ padding: "8px 10px" }}>
                    <Text sx={{ color: starHeader }}>
                        {criteria.name} ({Math.floor(Number(criteria.rating || 0))})
                    </Text>
                    <Box>
                        {[0, 1, 2, 3, 4].map(i => (
                            <FaStar
                                key={i}
                                size={16}
                                fill={i < Math.floor(Number(criteria.rating || 0)) ? "#FFB721" : "#8D8D8D"}
                            />
                        ))}
                    </Box>
                </Box>
            ))}
        </Box>
    );
});

const WorkerPerformancePage: React.FC = () => {
    const { loader, workerPerformance } = useSelector((state: RootState) => state?.rating);
    const { dataForTable } = useSelector((state: RootState) => state.masterData) as { dataForTable: IMasterDataForTable | null | undefined };
    const { auther } = useSelector((state: RootState) => state.auth);
    const dispatch = useDispatch();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams(DEFAULT_PARAMS);
    const [colorMode] = useColorMode();
    const starHeader = colorMode === 'light' ? "#0a0a0a" : "#dadada";

    const limit = parseInt(searchParams.get('limit') || "15");
    const currentPage = parseInt(searchParams.get('currentPage') || "1");

    // Role checks
    const roles: string[] = auther?.Role?.map((r) => r?.RoleName) || [];
    const isOnlyWorker = roles.length > 0 && roles.every(r => r === 'Task Worker');
    const isTaskWorker = roles.includes('Task Worker');
    const isTaskManager = roles.includes('Task Manager');
    const hasHigherRole = roles.some(r =>
        ['project manager', 'super admin', 'region admin', 'ceo', 'coo', 'principal (pe)'].includes(r?.toLowerCase()?.trim())
    );
    // Has both Task Worker AND Task Manager (but not a higher role)
    const isTaskManagerAndWorker = isTaskWorker && isTaskManager && !hasHigherRole;

    useEffect(() => {
        const columnsForApi = [
            { accessorKey: "no", filterType: "textbox" },
            { accessorKey: "workerName", filterType: "textbox" },
        ];
        let apiEndPoint = getApiEndPoint({ limit, currentPage }, location.search, columnsForApi);
        dispatch(getTradeWorkerPerformance(apiEndPoint));
    }, [limit, currentPage, location.search]);

    // Workers filtered by same department
    const workerOptions = useMemo(() => {
        const autherDepartments = auther?.departments || [];
        const isTaskManagerOrTeamLead = auther?.Role?.some((role) =>
            ['task manager', 'team lead'].includes(role?.RoleName?.toLowerCase()?.trim())
        );
        const hasHigherRole = auther?.Role?.some((role) =>
            ['project manager', 'super admin', 'region admin', 'ceo', 'coo', 'principal (pe)'].includes(role?.RoleName?.toLowerCase()?.trim())
        );
        const applyDepartmentFilter = isTaskManagerOrTeamLead && !hasHigherRole;
        const autherDeptIds = autherDepartments.map((d) => typeof d === 'object' ? (d as { _id: string })?._id : d).filter(Boolean);

        const workers = (dataForTable?.data?.allTaskWorkers || []).filter((worker: IMasterDataUser) => {
            if (!applyDepartmentFilter) return true;
            if (!autherDeptIds?.length) return true;
            const workerDeptIds = (worker?.departments || []).map((d) => typeof d === 'object' ? (d as { _id: string })?._id : d).filter(Boolean);
            return autherDeptIds.some((deptId) => workerDeptIds.includes(deptId));
        });

        if (workers.length === 0 && (dataForTable?.data?.allTaskWorkers || []).length > 0) {
            return (dataForTable?.data?.allTaskWorkers || []).map((val: IMasterDataUser) => ({
                value: val._id,
                label: `${val.FirstName} ${val.LastName}`
            }));
        }

        return workers.map((val: IMasterDataUser) => ({
            value: val._id,
            label: `${val.FirstName} ${val.LastName}`
        }));
    }, [dataForTable?.data?.allTaskWorkers, auther]);

    const columns = useMemo<ColumnDef<any>[]>(() => [
        {
            header: 'No',
            enableColumnFilter: false,
            accessorKey: "no",
            cell: ({ row }) => {
                return (
                    <Text variant='Poppins14Normal24lh'>{((currentPage - 1) * limit) + row.index + 1}</Text>
                )
            }
        },
        {
            header: 'Task Worker',
            accessorKey: "workerName",
            id: 'taskWorker',
            cell: ({ row }: { row: any }) => {
                return (
                    <Text>{row?.original?.workerName}</Text>
                )
            },
            // isOnlyWorker      → no filter
            // isTaskManagerAndWorker → dropdown filtered by same department, no Task Manager filter
            // otherwise (higher roles) → full dropdown
            ...(isOnlyWorker
                ? { enableColumnFilter: false }
                : { filterType: 'dropdown', options: workerOptions }
            )
        },
        {
            accessorKey: "totalProjects",
            header: 'Total Projects',
            cell: ({ row }) => {
                return (
                    <Text>{row?.original?.uniqueProjectCount}</Text>
                )
            },
        },
        {
            header: 'Criteria',
            accessorKey: "criteria",
            cell: ({ row }) => (
                <CriteriaCell
                    criteriaList={row.original.criteria}
                    starHeader={starHeader}
                />
            ),
        },

    ], [currentPage, limit, dataForTable, starHeader, workerOptions, isOnlyWorker, isTaskManagerAndWorker]);

    const activeWorkerIds = useMemo(() =>
        new Set((dataForTable?.data?.allTaskWorkers || []).map((w: { _id: string }) => w._id)),
        [dataForTable?.data?.allTaskWorkers]
    );

    const filteredData = useMemo(() =>
        (workerPerformance?.data || []).filter((row: any) => activeWorkerIds.has(row.workerNameRef)),
        [workerPerformance?.data, activeWorkerIds]
    );

    return (
        <div className='main-content'>
            <TabelHeadingTxt
                Title='Worker Performance'
                Count={workerPerformance?.count || 0}
                IsExportButtonShow={true}
                src={exprt}
                BtnTxt='Export'
                url='/worker-performance'
                ml={'60px'}
            />
            <Divider sx={{ color: '#D9D7DD' }} opacity={2} my={[3, 3, 4]} />
            <DataTable
                loader={loader}
                columns={columns}
                fixedColumn={true}
                data={filteredData}
                TotalLength={workerPerformance?.count || 0}
                perPageCount={limit}
                currentPage={currentPage}
                onPerPageChange={(itemsPerPage: string) => {
                    const parsedQuery = queryString.parse(location.search);
                    setSearchParams({
                        ...parsedQuery,
                        limit: itemsPerPage.toString(),
                        currentPage: "1"
                    });
                }}
                onPageChange={(value: any) => {
                    const parsedQuery = queryString.parse(location.search);
                    setSearchParams({
                        ...parsedQuery,
                        currentPage: value.toString(),
                    });
                }}
            />
        </div>
    );
};

export default WorkerPerformancePage;
